import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import Loader from 'components/Shared/Loader';
import { useDispatch, useSelector } from 'react-redux';
import UnsavedChangesModal from 'components/Shared/UnsavedChangesModal';
import AccountsSidebar from './widgets/AccountsSidebar';
import AccountContent from './widgets/AccountContent';
import EmptyAccountState from './widgets/EmptyAccountState';
import AddAccountModal from './widgets/AddAccountModal';
import CompactUserInfo from './widgets/CompactUserInfo';
import {
  updateManagedUserLocal,
  setSelectedAccount,
  resetUserAccountManagement,
} from 'redux_store/slices/userAccountManagementSlice';
import {
  removeUserAccountAccess,
  saveUserAccountChanges,
  fetchUserWithAccounts,
} from 'redux_store/api/endpoints/users';
import LoadingErrorWidget from '../../Shared/LoadingErrorWidget';
import { REQUEST_STATUS } from '../../../redux_store';
import USER_ROLES from '../constants';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    overflow: 'hidden', // Prevent outer scroll
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(4, 0, 2),
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 0, 1.5),
      gap: theme.spacing(1.5),
    },
  },
  leftColumn: {
    width: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    flexShrink: 0,
    minHeight: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('xl')]: {
      width: 380,
    },
    [theme.breakpoints.down('lg')]: {
      width: 340,
    },
    [theme.breakpoints.down('md')]: {
      width: 280,
      minWidth: 280,
    },
    [theme.breakpoints.down('sm')]: {
      width: 240,
      minWidth: 240,
    },
  },
  rightColumn: {
    flex: 1,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0, // Allow shrinking
    minHeight: 0, // Allow shrinking in height
  },
  sidebarContainer: {
    flex: 1,
    background: '#f8f9fa',
    borderRadius: 16,
    border: '2px solid #e1e4e8',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    minHeight: 0, // Important for flex children with overflow
  },
  errorContentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  // Non-DS layout styles
  nonDsContent: {
    flex: 1,
    background: 'white',
    borderRadius: 16,
    // boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    margin: theme.spacing(2, 0),
  },
}));

const UserAccountsManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userUuid, currentAccountUuid } = useParams();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const { isDistributionSponsor } = useSelector((state) => state.user);

  // Redux state
  const {
    managedUser,
    userAccounts,
    managedUserStatus,
    selectedAccount,
    userAccountsStatus,
  } = useSelector((state) => state.userAccountManagement);

  // Local UI state only
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingAccountId, setPendingAccountId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'switch' or 'addAccount'
  const [shouldOpenAddAccount, setShouldOpenAddAccount] = useState(false);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});
  const formikRef = useRef();
  const loading = (managedUserStatus === REQUEST_STATUS.loading ||
      (managedUserStatus === REQUEST_STATUS.idle && !managedUser)) ||
    (userAccountsStatus === REQUEST_STATUS.loading ||
      (userAccountsStatus === REQUEST_STATUS.idle && !userAccounts));

  useEffect(() => {
    dispatch(fetchUserWithAccounts({ userUuid, accountUuid: currentAccountUuid }));

    // Cleanup on unmount
    return () => {
      dispatch(resetUserAccountManagement());
    };
  }, [userUuid, currentAccountUuid, dispatch, titles]);

  const handleAccountSelect = (accountId) => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges && accountId !== selectedAccount) {
      setPendingAccountId(accountId);
      setPendingAction('switch');
      setShowModal(true);
      return;
    }

    dispatch(setSelectedAccount(accountId));
  };

  const handleUnsavedChangesWarning = (action) => {
    setPendingAction(action);
    setShowModal(true);
  };

  const handleUserUpdate = (updatedUserData) => {
    dispatch(updateManagedUserLocal(updatedUserData));
  };


  const handleRemoveAccount = (accountUUID) => {
    // Find the account by accountUUID
    const accountToRemove = userAccounts.find(acc => acc.accountUUID === accountUUID);
    if (!accountToRemove) {
      return;
    }

    // Check if this is the last account
    const isLastAccount = userAccounts.length === 1;

    // Dispatch the Redux thunk - it will handle all the logic
    dispatch(removeUserAccountAccess({
      accountId: accountToRemove.id || accountToRemove.accountUUID, // Use accountUUID if id doesn't exist
      accountUuid: accountToRemove.accountUUID,
      userUuid: managedUser.uuid,
      isLastAccount, // Pass this info to the thunk
    }));
  };

  const handleSaveChanges = (formValues, callback) => {
    // Find the selected account by accountUUID
    const currentAccount = userAccounts.find(acc => acc.accountUUID === selectedAccount);
    if (!currentAccount || !managedUser) {
      return;
    }

    // State will be updated only if API call succeeds
    dispatch(saveUserAccountChanges({
      selectedAccountUuid: currentAccount.accountUUID,
      userUuid: managedUser.uuid,
      formValues, // Pass formValues instead of patches
      callback,
    }));
  };

  const handleDirtyChange = (isDirty) => {
    setHasUnsavedChanges(isDirty);
  };

  const handleKeepEditing = () => {
    setShowModal(false);
    setPendingAccountId(null);
    setPendingAction(null);
  };

  const getInitialValues = () => {
    // Safe fallback if currentAccount is undefined
    if (!currentSelectedAccount) {
      return {
        role: USER_ROLES.PROVIDER,
        providerTitle: '',
        providerNPINumber: '',
        allowedAccounts: [],
        userPermissions: {
          managesUsers: false,
          receiveClaimsEmails: false,
          administerClaims: false,
          administerClaimReports: false,
          receiveSurveysEmails: false,
          imminentRiskEmails: false,
          managesAlert: false,
          manageChildAccounts: false,
        },
      };
    }

    // Get user permissions object
    const permissionsSource = currentSelectedAccount.userPermissions || {};

    const initialValues = {
      role: currentSelectedAccount.role?.toLowerCase() || USER_ROLES.PROVIDER,
      providerTitle: currentSelectedAccount.providerTitle || '',
      providerNPINumber: currentSelectedAccount.providerNPINumber || '',
      allowedAccounts: currentSelectedAccount.allowedAccounts || [],
      userPermissions: {
        managesUsers: permissionsSource.managesUsers || false,
        receiveClaimsEmails: permissionsSource.receiveClaimsEmails || false,
        administerClaims: permissionsSource.administerClaims || false,
        administerClaimReports: permissionsSource.administerClaimReports || false,
        receiveSurveysEmails: permissionsSource.receiveSurveysEmails || false,
        imminentRiskEmails: permissionsSource.imminentRiskEmails || false,
        managesAlert: permissionsSource.managesAlert || false,
        manageChildAccounts: permissionsSource.manageChildAccounts || false,
      },
    };

    return initialValues;
  };

  const handleDiscardChanges = () => {
    setShowModal(false);

    if (pendingAction === 'switch' && pendingAccountId) {
      dispatch(setSelectedAccount(pendingAccountId));
    } else if (pendingAction === 'addAccount') {
      // Reset unsaved changes state before opening add account modal
      setBackendErrors({});
      const values = getInitialValues();
      formikRef.current.resetForm({
        values: values,
        initialValues: values,
      });
      setHasUnsavedChanges(false);
      // Signal to open add account popover
      setShouldOpenAddAccount(true);
    }

    setPendingAccountId(null);
    setPendingAction(null);
  };

  const getCurrentAccount = () => {
    return userAccounts.find(acc => acc.accountUUID === selectedAccount) || userAccounts[0];
  };

  if (loading) {
    return (
      <ContentWrapper>
        <Loader fullHeight />
      </ContentWrapper>
    );
  }

  if (managedUserStatus === REQUEST_STATUS.failed && currentAccountUuid) {
    return (
      <ContentWrapper>
        <div className={classes.errorContentContainer}>
          <LoadingErrorWidget onReload={() => {
            if (userUuid) {
              dispatch(fetchUserWithAccounts({ userUuid, accountUuid: currentAccountUuid }));
            }
          }} />
        </div>
      </ContentWrapper>
    );
  }

  const currentSelectedAccount = getCurrentAccount();
  const hasNoAccounts = !userAccounts || userAccounts.length === 0;

  // Create compact user info for actions
  const userInfoActions = managedUser ? (
    <CompactUserInfo
      userData={managedUser}
      onUserUpdate={handleUserUpdate}
      accountUuid={currentAccountUuid}
      showEditButton={!hasNoAccounts}
    />
  ) : null;

  return (
    <ContentWrapper
      titleText={titles.manageUserAccounts || 'User Account Access'}
      actions={userInfoActions}
      containerStyle={{
        overflow: 'auto',
        height: 'calc(100% - 35px)',
        boxSizing: 'border-box',
        padding: 0,
      }}
    >
      {hasNoAccounts ? (
        /* Empty State with integrated user header */
        <EmptyAccountState
          onAddAccount={() => setIsAddAccountModalOpen(true)}
          userData={managedUser}
          onUserUpdate={handleUserUpdate}
        />
      ) : !isDistributionSponsor ? (
        /* Non-DS layout - single column without header */
        <div className={classes.nonDsContent}>
          <AccountContent
            currentAccount={userAccounts?.[0]} // Use the only account (current account)
            onRemoveAccount={handleRemoveAccount}
            onSaveChanges={handleSaveChanges}
            onDirtyChange={handleDirtyChange}
            regularLayout={true} // Hide remove button for non-DS accounts
            totalAccountsCount={userAccounts?.length || 0}
            backendErrors={backendErrors}
            setBackendErrors={setBackendErrors}
            formikRef={formikRef}
            getInitialValues={getInitialValues}
          />
        </div>
      ) : (
        /* DS layout - Two-column layout with more space for accounts */
        <div className={classes.contentContainer}>
          {/* Left Column - Accounts Sidebar takes full height */}
          <div className={classes.leftColumn}>
            <div className={classes.sidebarContainer}>
              <AccountsSidebar
                accounts={userAccounts}
                selectedAccount={selectedAccount}
                onAccountSelect={handleAccountSelect}
                userUuid={userUuid}
                hasUnsavedChanges={hasUnsavedChanges}
                onUnsavedChangesWarning={handleUnsavedChangesWarning}
                shouldOpenAddAccount={shouldOpenAddAccount}
                setShouldOpenAddAccount={setShouldOpenAddAccount}
                accountUuid={currentAccountUuid}
                setIsModalOpen={() => setIsAddAccountModalOpen(true)}
              />
            </div>
          </div>

          {/* Right Column - Account Content */}
          <div className={classes.rightColumn}>
            <AccountContent
              currentAccount={currentSelectedAccount}
              onRemoveAccount={handleRemoveAccount}
              onSaveChanges={handleSaveChanges}
              onDirtyChange={handleDirtyChange}
              totalAccountsCount={userAccounts?.length || 0}
              backendErrors={backendErrors}
              setBackendErrors={setBackendErrors}
              formikRef={formikRef}
              getInitialValues={getInitialValues}
            />
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={showModal}
        onDiscard={handleDiscardChanges}
        onKeepEditing={handleKeepEditing}
        title="Unsaved changes"
        message={
          pendingAction === 'switch'
            ? `You have unsaved changes. Switching accounts will lose your changes.`
            : `You have unsaved changes. Adding a new account will lose your changes.`
        }
        discardButtonText={pendingAction === 'switch' ? 'Switch anyway' : 'Add anyway'}
        keepEditingButtonText="Keep editing"
      />

      {/* Add Account Modal */}
      <AddAccountModal
        open={isAddAccountModalOpen}
        onClose={() => setIsAddAccountModalOpen(false)}
        userUuid={userUuid}
        accountUuid={currentAccountUuid}
      />


    </ContentWrapper>
  );
};

export default UserAccountsManagement;
